import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import { MarketingSummary } from '../utils/azureOpenAI';
import { Release } from '../types';

interface MarketingSummaryDialogProps {
  open: boolean;
  onClose: () => void;
  summary: MarketingSummary | null;
  loading: boolean;
  error: string | null;
  applicationName: string;
  releaseVersion: string;
  releases: Release[];
  onVersionChange: (releaseId: string) => void;
}

const MarketingSummaryDialog: React.FC<MarketingSummaryDialogProps> = ({
  open,
  onClose,
  summary,
  loading,
  error,
  applicationName,
  releaseVersion,
  releases,
  onVersionChange
}) => {
  const [selectedReleaseId, setSelectedReleaseId] = useState<string>('');

  // Filter to only show Current status or Production type releases
  const availableReleases = releases.filter(r => r.status === 'Current');

  // Reset selection when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedReleaseId('');
    }
  }, [open]);

  const handleGenerateSummary = () => {
    if (selectedReleaseId) {
      onVersionChange(selectedReleaseId);
    }
  };
  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    // You could add a toast notification here
    console.log(`${label} copied to clipboard`);
  };

  const copyAllContent = () => {
    if (!summary) return;
    
    const allContent = `
${summary.headline}
${'='.repeat(summary.headline.length)}

${summary.summary}

KEY FEATURES:
${summary.keyFeatures.map((f, i) => `${i + 1}. ${f}`).join('\n')}

SOCIAL MEDIA POST:
${summary.socialMediaPost}

EMAIL SUBJECT:
${summary.emailSubject}

EMAIL BODY:
${summary.emailBody}
    `.trim();
    
    copyToClipboard(allContent, 'All content');
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          margin: { xs: 2, sm: 3 },
          maxHeight: { xs: 'calc(100% - 32px)', sm: 'calc(100% - 64px)' },
          width: { xs: 'calc(100% - 32px)', sm: '100%' },
          minHeight: { xs: 'auto', md: '70vh' }
        }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6">
              Release Summary
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {applicationName}
            </Typography>
          </Box>
          {summary && (
            <Chip 
              label="AI Generated" 
              color="primary" 
              size="small"
            />
          )}
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        {!summary && !loading && !error && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 300, gap: 3 }}>
            <Typography variant="h6" color="text.secondary">
              Select a release version to generate marketing summary
            </Typography>
            <FormControl sx={{ minWidth: 300 }}>
              <InputLabel>Select Version</InputLabel>
              <Select
                value={selectedReleaseId}
                label="Select Version"
                onChange={(e) => setSelectedReleaseId(e.target.value)}
              >
                {availableReleases.map((release) => (
                  <MenuItem key={release.id} value={release.id}>
                    {release.version} ({release.status === 'Current' ? 'Current' : 'Production'}) - {new Date(release.releaseDate).toLocaleDateString()}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {availableReleases.length === 0 && (
              <Alert severity="warning">
                No Current or Production releases available
              </Alert>
            )}
            <Button
              variant="contained"
              size="large"
              onClick={handleGenerateSummary}
              disabled={!selectedReleaseId}
            >
              Generate Summary
            </Button>
          </Box>
        )}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
            <Box sx={{ textAlign: 'center' }}>
              <CircularProgress />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Summarizing content with AI...
              </Typography>
            </Box>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {summary && !loading && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Headline Section */}
            <Paper elevation={2} sx={{ p: 2, bgcolor: 'action.hover' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
                <Typography variant="overline" color="primary" fontWeight="bold">
                  Headline
                </Typography>
                <Button
                  size="small"
                  startIcon={<CopyIcon />}
                  onClick={() => copyToClipboard(summary.headline, 'Headline')}
                >
                  Copy
                </Button>
              </Box>
              <Typography variant="h5" fontWeight="bold" color="text.primary">
                {summary.headline}
              </Typography>
            </Paper>

            {/* Summary Section */}
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="overline" color="primary" fontWeight="bold">
                  Summary
                </Typography>
                <Button
                  size="small"
                  startIcon={<CopyIcon />}
                  onClick={() => copyToClipboard(summary.summary, 'Summary')}
                >
                  Copy
                </Button>
              </Box>
              <Typography variant="body1">
                {summary.summary}
              </Typography>
            </Box>

            <Divider />

            {/* Key Features Section */}
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="overline" color="primary" fontWeight="bold">
                  Key Features
                </Typography>
                <Button
                  size="small"
                  startIcon={<CopyIcon />}
                  onClick={() => copyToClipboard(summary.keyFeatures.join('\n• '), 'Key Features')}
                >
                  Copy
                </Button>
              </Box>
              <Box component="ul" sx={{ pl: 2 }}>
                {summary.keyFeatures.map((feature, index) => (
                  <Typography component="li" variant="body2" key={index} sx={{ mb: 1 }}>
                    {feature}
                  </Typography>
                ))}
              </Box>
            </Box>

            <Divider />

            {/* Social Media Section */}
            <Paper elevation={1} sx={{ p: 2, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="overline" fontWeight="bold" sx={{ color: 'inherit', opacity: 0.9 }}>
                  Social Media Post
                </Typography>
                <Button
                  size="small"
                  startIcon={<CopyIcon />}
                  onClick={() => copyToClipboard(summary.socialMediaPost, 'Social Media Post')}
                  sx={{ color: 'inherit' }}
                >
                  Copy
                </Button>
              </Box>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: 'inherit' }}>
                {summary.socialMediaPost}
              </Typography>
            </Paper>

            <Divider />

            {/* Email Section */}
            <Box>
              <Typography variant="overline" color="primary" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EmailIcon fontSize="small" />
                Email Content
              </Typography>
              
              <Paper elevation={1} sx={{ p: 2, mt: 2, bgcolor: 'secondary.main', color: 'secondary.contrastText' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ color: 'inherit' }}>
                    Subject Line
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<CopyIcon />}
                    onClick={() => copyToClipboard(summary.emailSubject, 'Email Subject')}
                    sx={{ color: 'inherit' }}
                  >
                    Copy
                  </Button>
                </Box>
                <Typography variant="body2" sx={{ color: 'inherit' }}>
                  {summary.emailSubject}
                </Typography>
              </Paper>

              <Paper elevation={1} sx={{ p: 2, mt: 2, bgcolor: 'background.default' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2" fontWeight="bold" color="text.primary">
                    Email Body
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<CopyIcon />}
                    onClick={() => copyToClipboard(summary.emailBody, 'Email Body')}
                  >
                    Copy
                  </Button>
                </Box>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }} color="text.primary">
                  {summary.emailBody}
                </Typography>
              </Paper>
            </Box>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>
          Close
        </Button>
        {summary && (
          <Button 
            variant="contained" 
            startIcon={<CopyIcon />}
            onClick={copyAllContent}
          >
            Copy All Content
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default MarketingSummaryDialog;
